import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, KeyRound } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Switch from '../ui/Switch';
import ListRow from '../ui/ListRow';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';

// HD-5: Account on src/ui (DESIGN rev 2.1 §3.1). Same calls as before: profile PATCH (re-issues tokens through
// refreshSession), notification event types + preferences GET/PUT. Password change lives on /change-password.
export default function AccountSettings() {
  const { user, refreshSession } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  usePageHeader({ title: 'Account', meta: user?.email });

  // ── Display name ──────────────────────────────────────────────────────────
  const [name,       setName]       = useState(user?.name || '');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError,  setNameError]  = useState(null);

  async function handleNameSave(e) {
    e.preventDefault();
    if (!name.trim()) { setNameError('Enter the name the team should see.'); return; }
    if (name.trim() === user?.name) return;
    setNameSaving(true);
    setNameError(null);
    try {
      const res = await api.patch('/api/auth/profile', { name: name.trim() });
      refreshSession(res.data.accessToken, res.data.refreshToken);
      toast('Name saved.');
    } catch (err) {
      setNameError(err.response?.data?.error || 'The name did not save. Check the connection and try again.');
    } finally {
      setNameSaving(false);
    }
  }

  // ── Notification preferences ─────────────────────────────────────────────
  const [notifPrefs,   setNotifPrefs]   = useState(null);  // { [eventType]: { eventType, emailEnabled, inAppEnabled } }
  const [notifTypes,   setNotifTypes]   = useState([]);    // [{ type, label, description, roles }]
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifFailed,  setNotifFailed]  = useState(false);
  const [notifSaving,  setNotifSaving]  = useState(false);

  function loadNotif() {
    setNotifLoading(true);
    setNotifFailed(false);
    Promise.all([
      api.get('/api/notifications/event-types'),
      api.get('/api/notifications/event-preferences'),
    ]).then(([typesRes, prefsRes]) => {
      setNotifTypes(typesRes.data);
      const map = {};
      prefsRes.data.forEach(p => { map[p.eventType] = p; });
      setNotifPrefs(map);
    }).catch(() => setNotifFailed(true)).finally(() => setNotifLoading(false));
  }
  useEffect(loadNotif, []);

  function togglePref(eventType, channel) {
    setNotifPrefs(prev => ({
      ...prev,
      [eventType]: { ...prev[eventType], eventType, [channel]: !(prev[eventType]?.[channel] ?? true) },
    }));
  }

  async function handleNotifSave() {
    setNotifSaving(true);
    try {
      const payload = Object.values(notifPrefs).map(p => ({
        eventType:    p.eventType,
        emailEnabled: p.emailEnabled ?? true,
        inAppEnabled: p.inAppEnabled ?? true,
      }));
      await api.put('/api/notifications/event-preferences', payload);
      toast('Notification preferences saved.');
    } catch {
      toast('Preferences did not save. Try again.', { tone: 'danger' });
    } finally {
      setNotifSaving(false);
    }
  }

  // Only event types relevant to the current user's roles
  const userRoles = user?.roles || [];
  const visibleTypes = notifTypes.filter(t => t.roles.some(r => userRoles.includes(r)));

  return (
    <div className="ui ui-page ui-page--narrow">
      <section className="ui-section" aria-labelledby="acct-name">
        <h2 className="ui-section__title" id="acct-name">Display name</h2>
        <form className="ui-region ui-region--pad ui-stack" onSubmit={handleNameSave}>
          <Input label="Name" value={name} error={nameError} autoComplete="name" placeholder="Your display name"
            onChange={e => { setName(e.target.value); setNameError(null); }} />
          <div className="ui-cluster">
            <Button type="submit" variant="primary" loading={nameSaving} disabled={!name.trim() || name.trim() === user?.name}>Save name</Button>
          </div>
        </form>
      </section>

      <section className="ui-section" aria-labelledby="acct-password">
        <h2 className="ui-section__title" id="acct-password">Password</h2>
        <div className="ui-region ui-region--pad ui-stack">
          <p className="ui-p ui-dense ui-text-2">Change the password you sign in with. You will need the current one.</p>
          <div className="ui-cluster">
            <Button variant="secondary" icon={KeyRound} onClick={() => navigate('/change-password')}>Change password</Button>
          </div>
        </div>
      </section>

      <section className="ui-section" aria-labelledby="acct-info">
        <h2 className="ui-section__title" id="acct-info">Account details</h2>
        <div className="ui-region ui-region--pad ui-stack">
          <dl className="ui-kv">
            <dt>Email</dt><dd>{user?.email || 'Not set'}</dd>
            <dt>Initials</dt><dd>{user?.initials || 'Not set'}</dd>
            <dt>Roles</dt><dd>{user?.roles?.join(', ') || 'None'}</dd>
          </dl>
          <p className="ui-p ui-dense ui-text-3">Only an admin can change your email, initials or roles.</p>
        </div>
      </section>

      <section className="ui-section" aria-labelledby="acct-notif">
        <h2 className="ui-section__title" id="acct-notif">Notifications</h2>
        <p className="ui-section__note">Choose which notifications you get in the app and by email. Password resets and shoot assignments are always emailed.</p>
        <div className="ui-region">
          {notifLoading ? (
            <div className="ui-list">{[0, 1, 2].map(i => <ListRow key={i} title={<Skeleton width="45%" />} meta={<Skeleton width="70%" />} />)}</div>
          ) : notifFailed ? (
            <ErrorState title="Preferences did not load" message="The server did not answer. Try again in a moment." onRetry={loadNotif} />
          ) : visibleTypes.length === 0 ? (
            <EmptyState message="None of your roles has notifications you can set. Ask an admin if you expect some." />
          ) : (
            <div className="ui-list">
              {visibleTypes.map(t => {
                const pref = notifPrefs?.[t.type] || {};
                const label = clean(t.label);
                return (
                  <ListRow key={t.type} title={label} meta={clean(t.description)} trailing={<>
                    <Switch label="In app" aria-label={`In app: ${label}`} checked={pref.inAppEnabled ?? true} onChange={() => togglePref(t.type, 'inAppEnabled')} />
                    <Switch label="Email" aria-label={`Email: ${label}`} checked={pref.emailEnabled ?? true} onChange={() => togglePref(t.type, 'emailEnabled')} />
                  </>} />
                );
              })}
            </div>
          )}
        </div>
        {!notifLoading && !notifFailed && visibleTypes.length > 0 && (
          <div className="ui-cluster">
            <Button variant="secondary" loading={notifSaving} disabled={!notifPrefs} onClick={handleNotifSave}>Save preferences</Button>
          </div>
        )}
      </section>

      <section className="ui-section" aria-labelledby="acct-timeoff">
        <h2 className="ui-section__title" id="acct-timeoff">Time off</h2>
        <div className="ui-region ui-region--pad ui-stack">
          <p className="ui-p ui-dense ui-text-2">Request time off and see your balance.</p>
          <div className="ui-cluster">
            <Button variant="secondary" icon={CalendarDays} onClick={() => navigate('/time-off')}>Open time off</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
